import React, { useState, useEffect } from 'react';
import { Sun, Moon, Zap } from 'lucide-react';
import { motion } from 'framer-motion';

/*
  HitHow Node — "Farewell" edition
  Página de despedida. O node foi descontinuado... ao menos por enquanto.
  Stack: React + Tailwind + Framer Motion
*/

// --- Translations ---
const translations = {
  en: {
    eyebrow: 'HitHow Node',
    heading: 'An incredibly beautiful journey.',
    body: 'The HitHow Lightning node is being discontinued… at least for now.',
    thanks: 'Thank you to everyone who was part of this journey.',
    footer: 'See you on the Lightning Network.',
  },
  pt: {
    eyebrow: 'HitHow Node',
    heading: 'Uma jornada incrivelmente linda.',
    body: 'O node Lightning HitHow está sendo descontinuado… ao menos por enquanto.',
    thanks: 'Obrigado a todos que fizeram parte dessa jornada.',
    footer: 'Até logo na Lightning Network.',
  },
  es: {
    eyebrow: 'HitHow Node',
    heading: 'Un viaje increíblemente hermoso.',
    body: 'El nodo Lightning HitHow está siendo descontinuado… al menos por ahora.',
    thanks: 'Gracias a todos los que formaron parte de este viaje.',
    footer: 'Nos vemos en la Lightning Network.',
  },
};

const Logo = ({ size = 32, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <defs>
      <linearGradient id="logo_grad" x1="32" y1="0" x2="32" y2="64" gradientUnits="userSpaceOnUse">
        <stop stopColor="#2563eb" />
        <stop offset="1" stopColor="#7c3aed" />
      </linearGradient>
    </defs>
    <path d="M32 0L61.7 17V47L32 64L2.3 47V17L32 0Z" fill="url(#logo_grad)" />
    <path d="M38 10L18 32H30L25 54L46 28H34L38 10Z" fill="white" />
  </svg>
);

const Background = () => (
  <div className="fixed inset-0 z-[-1] overflow-hidden bg-gray-50 dark:bg-cyber-dark transition-colors duration-500">
    <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400/20 dark:bg-cyber-primary/20 rounded-full blur-[128px] animate-blob" />
    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-400/20 dark:bg-cyber-secondary/20 rounded-full blur-[128px] animate-blob" style={{ animationDelay: '2s' }} />
    <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-cyan-400/10 dark:bg-cyber-accent/10 rounded-full blur-[128px] animate-blob" style={{ animationDelay: '4s' }} />
    <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-soft-light" />
  </div>
);

const App = () => {
  const getInitialTheme = () => {
    const saved = localStorage.getItem('theme');
    if (saved === 'light' || saved === 'dark') return saved;
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  };

  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'pt');
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => { localStorage.setItem('lang', lang); }, [lang]);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');
  const t = translations[lang];

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, delay, ease: 'easeOut' },
  });

  return (
    <div className="min-h-screen text-gray-900 dark:text-gray-200 font-sans selection:bg-blue-200 dark:selection:bg-cyber-primary/30 transition-colors duration-500">
      <Background />

      {/* Top controls: theme + language */}
      <motion.header
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.4 }}
        className="absolute top-0 inset-x-0 z-50"
      >
        <div className="container mx-auto px-6 py-6 flex items-center justify-end gap-4">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 text-gray-600 dark:text-gray-300 transition-colors"
            aria-label="Toggle Theme"
          >
            {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          <div className="flex items-center gap-2">
            {['en', 'pt', 'es'].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`text-lg transition-transform hover:scale-110 ${lang === l ? 'opacity-100' : 'opacity-40 hover:opacity-100'}`}
                aria-label={`Switch to ${l}`}
              >
                {l === 'en' ? '🇺🇸' : l === 'pt' ? '🇧🇷' : '🇪🇸'}
              </button>
            ))}
          </div>
        </div>
      </motion.header>

      {/* Centered farewell message */}
      <main className="min-h-screen flex items-center justify-center px-6 text-center">
        <div className="max-w-2xl mx-auto flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="mb-8 animate-float"
          >
            <Logo size={72} />
          </motion.div>

          <motion.p
            {...fade(0.2)}
            className="text-sm font-mono uppercase tracking-[0.25em] text-blue-600 dark:text-cyber-accent mb-6"
          >
            {t.eyebrow}
          </motion.p>

          <motion.h1
            {...fade(0.4)}
            className="text-4xl md:text-6xl font-bold leading-tight mb-8 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-500 dark:from-cyber-primary dark:via-cyber-secondary dark:to-cyber-accent"
          >
            {t.heading}
          </motion.h1>

          <motion.p
            {...fade(0.6)}
            className="text-lg md:text-xl text-gray-600 dark:text-gray-400 leading-relaxed mb-4"
          >
            {t.body}
          </motion.p>

          <motion.p
            {...fade(0.8)}
            className="text-lg md:text-xl font-medium text-gray-800 dark:text-gray-200 leading-relaxed"
          >
            {t.thanks}
          </motion.p>

          <motion.div
            {...fade(1.1)}
            className="mt-12 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-500"
          >
            <Zap size={16} className="text-yellow-500 animate-pulse-slow" />
            <span>{t.footer}</span>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="absolute bottom-0 inset-x-0 py-8 text-center"
      >
        <p className="text-xs text-gray-400 dark:text-gray-600">
          © {new Date().getFullYear()} HitHow · hithow.com
        </p>
      </motion.footer>
    </div>
  );
};

export default App;
